import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  addDays,
  addMonths,
  earliestSlotDate,
  formatIso,
  monthWeekStart,
  startOfDay,
  startOfWeek,
  weekFocusDate,
  weekStartsOnSunday,
} from './calendarDates'

export function useCalendarView(getSlots) {
  const { locale } = useI18n()

  const view = ref('month')
  const isMonth = computed(() => view.value === 'month')
  const isDay = computed(() => view.value === 'day')
  const weekStart = ref(startOfDay(new Date()))
  const userMoved = ref(false)
  // Set when month → week opens a week clamped to the month; arrows drop it.
  const pinnedWeekStart = ref(null)

  watch(
    getSlots,
    (slots) => {
      if (userMoved.value) return
      const first = earliestSlotDate(slots)
      if (first) weekStart.value = startOfDay(first)
    },
    { immediate: true }
  )

  function dayMeta(date) {
    const todayIso = formatIso(new Date())
    const weekdayFormat = new Intl.DateTimeFormat(locale.value, { weekday: 'short' })
    return {
      iso: formatIso(date),
      weekday: weekdayFormat.format(date),
      dayNumber: date.getDate(),
      isToday: formatIso(date) === todayIso,
    }
  }

  const dayDays = computed(() => [dayMeta(weekStart.value)])

  const weekDays = computed(() => {
    const start = pinnedWeekStart.value ?? startOfWeek(weekStart.value, locale.value)
    return Array.from({ length: 7 }, (_, index) => dayMeta(addDays(start, index)))
  })

  const monthDays = computed(() => {
    const monthStart = new Date(weekStart.value.getFullYear(), weekStart.value.getMonth(), 1)
    const monthEnd = new Date(weekStart.value.getFullYear(), weekStart.value.getMonth() + 1, 0)
    const todayIso = formatIso(new Date())
    let cursor = startOfWeek(monthStart, locale.value)
    const days = []
    while (days.length < 42) {
      days.push({
        iso: formatIso(cursor),
        dayNumber: cursor.getDate(),
        inMonth: cursor.getMonth() === monthStart.getMonth(),
        isToday: formatIso(cursor) === todayIso,
      })
      cursor = addDays(cursor, 1)
      if (cursor > monthEnd && days.length % 7 === 0) break
    }
    return days
  })

  const outsideView = computed(() => {
    const days = isMonth.value ? monthDays.value : isDay.value ? dayDays.value : weekDays.value
    if (!days.length) return ''
    const first = days[0].iso
    const last = days[days.length - 1].iso
    let before = false
    let after = false
    for (const slot of getSlots() || []) {
      const date = slot?.scheduledLocal?.date
      if (!date) continue
      if (date < first) before = true
      else if (date > last) after = true
    }
    if (before && after) return 'outside-both'
    if (before) return 'outside-before'
    if (after) return 'outside-after'
    return ''
  })

  const monthLabel = computed(() => {
    const label = new Intl.DateTimeFormat(locale.value, {
      month: 'long',
      year: 'numeric',
    }).format(weekStart.value)
    return label.charAt(0).toUpperCase() + label.slice(1)
  })

  const weekdayLabels = computed(() => {
    const format = new Intl.DateTimeFormat(locale.value, { weekday: 'short' })
    const sunday = new Date(2024, 0, 7)
    const offset = weekStartsOnSunday(locale.value) ? 0 : 1
    return Array.from({ length: 7 }, (_, index) => format.format(addDays(sunday, offset + index)))
  })

  watch(view, (next, previous) => {
    pinnedWeekStart.value = null
    if (previous === 'month' && next === 'week') {
      weekStart.value = weekFocusDate(weekStart.value, getSlots())
      pinnedWeekStart.value = monthWeekStart(weekStart.value, locale.value)
    }
  })

  function shift(direction) {
    userMoved.value = true
    pinnedWeekStart.value = null
    if (view.value === 'month') weekStart.value = addMonths(weekStart.value, direction)
    else if (view.value === 'day') weekStart.value = addDays(weekStart.value, direction)
    else weekStart.value = addDays(startOfWeek(weekStart.value, locale.value), direction * 7)
  }

  return {
    view,
    isMonth,
    isDay,
    dayDays,
    weekDays,
    monthDays,
    monthLabel,
    weekdayLabels,
    outsideView,
    movePrev: () => shift(-1),
    moveNext: () => shift(1),
  }
}
